<script setup lang="ts">
import { ref, computed } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import axios from 'axios'

const props = defineProps<{
  visible: boolean
  /**
   * Cambio OBBLIGATORIO (l'amministratore ha impostato «Richiedi il cambio password al prossimo
   * accesso»): il dialog non si chiude, non c'è Annulla e la X spariscono. È il comportamento
   * del portale classico, che mostra il cambio forzato appena si entra.
   */
  forzato?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'success'): void
}>()

const auth = useAuthStore()
const router = useRouter()
const toast = useToast()
const { t } = useI18n()

/**
 * Via d'uscita dal cambio obbligatorio: esce e torna al login.
 * Serve perché il dialog richiede la PASSWORD ATTUALE — quella temporanea comunicata
 * dall'amministratore. Chi non l'ha sottomano, senza questo pulsante resterebbe bloccato in una
 * schermata che non può né completare né chiudere.
 */
const uscendo = ref(false)
async function esci(): Promise<void> {
  if (uscendo.value) return
  uscendo.value = true
  try {
    await auth.logout()
  } finally {
    await router.push('/login')
  }
}

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const error = ref<string | null>(null)
const loading = ref(false)

// ── Checklist LIVE delle regole (stessa grafica della pagina di reset password) ──────
interface Regola {
  label: string
  ok: boolean
}
const regole = computed<Regola[]>(() => {
  const p = newPassword.value
  const user = (auth.me?.username ?? '').toLowerCase()
  return [
    { label: t('layout.cambioPassword.regola816', 'Da 8 a 16 caratteri'), ok: p.length >= 8 && p.length <= 16 },
    { label: t('layout.cambioPassword.regolaMinuscola', 'Almeno una lettera minuscola (a-z)'), ok: /[a-z]/.test(p) },
    { label: t('layout.cambioPassword.regolaMaiuscola', 'Almeno una lettera MAIUSCOLA (A-Z)'), ok: /[A-Z]/.test(p) },
    { label: t('layout.cambioPassword.regolaNumero', 'Almeno un numero (0-9)'), ok: /[0-9]/.test(p) },
    {
      label: t('layout.cambioPassword.regolaNomeUtente', 'Non contiene il tuo nome utente'),
      ok: p.length > 0 && (user === '' || !p.toLowerCase().includes(user)),
    },
    { label: t('layout.cambioPassword.regolaDiversa', 'Diversa dalla password attuale'), ok: p.length > 0 && p !== currentPassword.value },
    { label: t('layout.cambioPassword.regolaCoincidono', 'Le due password coincidono'), ok: p.length > 0 && p === confirmPassword.value },
  ]
})
const tutteOk = computed(() => regole.value.every((r) => r.ok))

const robustezza = computed(() => {
  const p = newPassword.value
  if (!p) return { pct: 0, label: '', cls: '' }
  let score = 0
  if (p.length >= 8) score++
  if (p.length >= 12) score++
  if (/[a-z]/.test(p)) score++
  if (/[A-Z]/.test(p)) score++
  if (/[0-9]/.test(p)) score++
  if (/[^A-Za-z0-9]/.test(p)) score++
  if (score <= 3) return { pct: 33, label: t('layout.cambioPassword.debole', 'Debole'), cls: 'cp-str--weak' }
  if (score <= 4) return { pct: 66, label: t('layout.cambioPassword.media', 'Media'), cls: 'cp-str--mid' }
  return { pct: 100, label: t('layout.cambioPassword.robusta', 'Robusta'), cls: 'cp-str--strong' }
})

function resetForm(): void {
  currentPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  error.value = null
  loading.value = false
}

function close(): void {
  // In modalità obbligatoria non si esce: l'unica via è cambiare la password.
  if (props.forzato) return
  emit('update:visible', false)
  resetForm()
}

async function submit(): Promise<void> {
  error.value = null
  if (!currentPassword.value || !tutteOk.value) return

  loading.value = true
  try {
    await auth.changePassword(currentPassword.value, newPassword.value, confirmPassword.value)
    toast.add({
      severity: 'success',
      summary: t('layout.cambioPassword.aggiornata', 'Password aggiornata'),
      detail: t('layout.cambioPassword.aggiornataDettaglio', "La password è stata modificata. Riceverai un'e-mail di conferma."),
      life: 4000,
    })
    emit('success')
    close()
  } catch (err) {
    if (axios.isAxiosError(err)) {
      error.value =
        (err.response?.data as { message?: string })?.message ??
        t('layout.cambioPassword.erroreCambio', 'Errore durante il cambio password. Verifica la password attuale e riprova.')
    } else {
      error.value = t('layout.cambioPassword.erroreImprevisto', 'Errore imprevisto. Riprova.')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Dialog
    :header="props.forzato ? t('layout.cambioPassword.obbligatorio', 'Cambio password obbligatorio') : t('layout.cambioPassword.cambia', 'Cambia password')"
    :visible="props.visible"
    modal
    :style="{ width: '440px' }"
    :draggable="false"
    :closable="!props.forzato"
    :close-on-escape="!props.forzato"
    @update:visible="close"
  >
    <form
      class="cp-form"
      @submit.prevent="submit"
    >
      <div class="cp-brand">
        <img
          src="/images/immigreer-logo.png"
          alt="Immigreer"
          class="cp-logo"
        >
      </div>
      <p class="cp-hint">
        <template v-if="props.forzato">
          <strong>{{ t('layout.cambioPassword.forzatoTitolo', 'Devi scegliere una nuova password prima di continuare.') }}</strong>
          {{ t('layout.cambioPassword.forzatoTesto', "L'amministratore ha reimpostato il tuo accesso e richiesto il cambio al primo ingresso. La nuova password varrà sia sul nuovo portale sia sul portale classico.") }}
        </template>
        <template v-else>
          {{ t('layout.cambioPassword.testo', "Scegli una nuova password. Riceverai un'e-mail di conferma; la nuova password varrà sia sul nuovo portale sia sul portale classico.") }}
        </template>
      </p>

      <div
        id="field-current-password"
        class="field"
      >
        <label for="cp-current">{{ t('layout.cambioPassword.attuale', 'Password attuale') }}</label>
        <Password
          id="cp-current"
          v-model="currentPassword"
          name="currentPassword"
          :feedback="false"
          toggle-mask
          fluid
          input-class="w-full"
          autocomplete="current-password"
        />
      </div>

      <div
        id="field-new-password"
        class="field"
      >
        <label for="cp-new">{{ t('layout.cambioPassword.nuova', 'Nuova password') }}</label>
        <Password
          id="cp-new"
          v-model="newPassword"
          name="newPassword"
          :feedback="false"
          toggle-mask
          fluid
          input-class="w-full"
          autocomplete="new-password"
        />
        <div
          v-if="newPassword"
          class="cp-str"
        >
          <div class="cp-str-track">
            <div
              class="cp-str-bar"
              :class="robustezza.cls"
              :style="{ width: robustezza.pct + '%' }"
            />
          </div>
          <span
            class="cp-str-label"
            :class="robustezza.cls"
          >{{ robustezza.label }}</span>
        </div>
      </div>

      <div
        id="field-confirm-password"
        class="field"
      >
        <label for="cp-confirm">{{ t('layout.cambioPassword.conferma', 'Conferma nuova password') }}</label>
        <Password
          id="cp-confirm"
          v-model="confirmPassword"
          name="confirmPassword"
          :feedback="false"
          toggle-mask
          fluid
          input-class="w-full"
          autocomplete="new-password"
        />
      </div>

      <div
        class="cp-rules"
        data-sezione="regole-password"
      >
        <div class="cp-rules-title">
          {{ t('layout.cambioPassword.regole', 'Regole della password') }}
        </div>
        <ul class="cp-rules-list">
          <li
            v-for="r in regole"
            :key="r.label"
            class="cp-rule"
            :class="{ 'cp-rule--ok': r.ok, 'cp-rule--ko': !r.ok && newPassword.length > 0 }"
          >
            <i :class="r.ok ? 'pi pi-check-circle' : 'pi pi-circle'" />
            <span>{{ r.label }}</span>
          </li>
        </ul>
      </div>

      <Message
        v-if="error"
        severity="error"
        :closable="false"
        class="text-sm"
      >
        {{ error }}
      </Message>

      <div class="flex justify-end gap-2 pt-1">
        <Button
          v-if="props.forzato"
          :label="t('layout.cambioPassword.esci', 'Esci')"
          icon="pi pi-sign-out"
          severity="secondary"
          outlined
          type="button"
          :loading="uscendo"
          @click="esci"
        />
        <Button
          v-if="!props.forzato"
          :label="t('common.annulla', 'Annulla')"
          text
          severity="secondary"
          type="button"
          @click="close"
        />
        <Button
          :label="t('layout.cambioPassword.cambia', 'Cambia password')"
          icon="pi pi-lock"
          type="submit"
          :loading="loading"
          :disabled="!currentPassword || !tutteOk"
        />
      </div>
    </form>
  </Dialog>
</template>

<style scoped>
.cp-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 0.25rem;
}
.cp-brand {
  display: flex;
  justify-content: center;
}
.cp-logo {
  height: 38px;
  width: auto;
  max-width: 60%;
  object-fit: contain;
}
.cp-hint {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
}

/* Barra di robustezza */
.cp-str {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
}
.cp-str-track {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--p-surface-200);
  overflow: hidden;
}
.cp-str-bar {
  height: 100%;
  border-radius: 3px;
  transition:
    width 0.25s ease,
    background 0.25s ease;
}
.cp-str-label {
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 4rem;
  text-align: right;
}
.cp-str-bar.cp-str--weak {
  background: var(--p-red-500);
}
.cp-str-bar.cp-str--mid {
  background: var(--p-orange-500);
}
.cp-str-bar.cp-str--strong {
  background: var(--p-green-500);
}
.cp-str-label.cp-str--weak {
  color: var(--p-red-500);
}
.cp-str-label.cp-str--mid {
  color: var(--p-orange-500);
}
.cp-str-label.cp-str--strong {
  color: var(--p-green-600);
}

/* Checklist regole */
.cp-rules {
  background: var(--p-surface-50);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
.cp-rules-title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--p-text-muted-color);
  margin-bottom: 0.5rem;
}
.cp-rules-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.cp-rule {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
  transition: color 0.15s ease;
}
.cp-rule i {
  font-size: 0.9rem;
}
.cp-rule--ok {
  color: var(--p-green-600);
}
.cp-rule--ok i {
  color: var(--p-green-500);
}
.cp-rule--ko {
  color: var(--p-text-color);
}
.cp-rule--ko i {
  color: var(--p-red-400);
}
</style>
